import { useEffect, useState } from "react";
import { Subject } from "../types";
import {
  fetchSubjects,
  createSubject,
  updateSubject,
  deleteSubject,
} from "../services/subjectsService";

export const useSubjects = () => {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchSubjects();
        setSubjects(data);
      } catch (err) {
        setError("Failed to load subjects.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const isDuplicateName = (name: string, excludeCode?: string) => {
    const normalized = name.trim().toLowerCase();
    return subjects.some(
      (subject) =>
        subject.name.trim().toLowerCase() === normalized && subject.code !== excludeCode
    );
  };

  const create = async (code: string, name: string, description: string) => {
    setSaving(true);
    setError(null);
    try {
      const created = await createSubject({ code, name: name.trim(), description: description.trim() });
      setSubjects((prev) => [...prev, created]);
      return true;
    } catch (err) {
      setError("Failed to create subject.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const update = async (code: string, name: string, description: string) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await updateSubject(code, { name: name.trim(), description: description.trim() });
      setSubjects((prev) => prev.map((subject) => (subject.code === code ? updated : subject)));
      return true;
    } catch (err) {
      setError("Failed to update subject.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const remove = async (code: string) => {
    setDeleting(true);
    setError(null);
    try {
      await deleteSubject(code);
      setSubjects((prev) => prev.filter((subject) => subject.code !== code));
      return true;
    } catch (err) {
      setError("Failed to delete subject.");
      return false;
    } finally {
      setDeleting(false);
    }
  };

  return {
    subjects,
    loading,
    saving,
    deleting,
    error,
    isDuplicateName,
    actions: {
      create,
      update,
      remove,
    },
  };
};
